import { isAxiosError } from 'axios'
import {
  AlertCircle,
  Edit,
  Eye,
  Plus,
  RefreshCw,
  Tag,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { PromotionConflictDetails } from '@/app/components/promotions/PromotionConflictDetails'
import { PromotionFormModal } from '@/app/components/promotions/PromotionFormModal'
import { PromotionProductsDialog } from '@/app/components/promotions/PromotionProductsDialog'
import {
  formatPromotionRule,
  promotionChannelLabels,
  promotionLifecycleLabels,
  promotionScopeLabels,
  promotionTypeLabels,
  toPromotionEndDateValue,
  toPromotionStartDateValue,
} from '@/app/components/promotions/promotion-form'
import { ConfirmationModal } from '@/app/components/ui/ConfirmationModal'
import {
  type FilterType,
  GlobalFilterBar,
} from '@/app/components/ui/GlobalFilterBar'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog'
import { GlobalPaginator } from '@/app/components/ui/global-paginator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select'
import { TableSkeleton } from '@/app/components/ui/table-skeleton'
import {
  type PromotionApiError,
  type PromotionChannel,
  type PromotionConflict,
  type PromotionLifecycle,
  type PromotionListItem,
  type PromotionOption,
  type PromotionScope,
  type PromotionType,
  promotionService,
} from '@/app/services/promotion.service'
import { sucursalService } from '@/app/services/sucursal.service'
import { useSessionStore } from '@/app/store/auth/auth.store'

const itemsPerPage = 15

const dateFormatter = new Intl.DateTimeFormat('es-MX', {
  dateStyle: 'medium',
})

const lifecycleBadgeClasses: Record<PromotionLifecycle, string> = {
  scheduled: 'bg-blue-100 text-blue-700',
  active: 'bg-[#DCFCE6] text-[#008438]',
  expired: 'bg-slate-100 text-slate-700',
  inactive: 'bg-[#FFE2E2] text-[#C10008]',
}

function formatDate(value: string) {
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date)
}

function normalizeSucursals(response: unknown): PromotionOption[] {
  if (!response || typeof response !== 'object') return []
  const envelope = response as { data?: unknown }
  const firstLevel = envelope.data ?? response
  if (Array.isArray(firstLevel)) return firstLevel as PromotionOption[]
  if (!firstLevel || typeof firstLevel !== 'object') return []
  const paginator = firstLevel as { data?: unknown }
  return Array.isArray(paginator.data)
    ? (paginator.data as PromotionOption[])
    : []
}

function scopeSummary(promotion: PromotionListItem): string {
  if (promotion.scope_type === 'global') return 'Todas las sucursales'
  return `${promotion.sucursals_count} ${promotion.sucursals_count === 1 ? 'sucursal' : 'sucursales'}`
}

function applicabilitySummary(promotion: PromotionListItem): string {
  const rules = [
    promotion.products_count > 0
      ? `${promotion.products_count} directos`
      : null,
    promotion.categories_count > 0
      ? `${promotion.categories_count} categorías`
      : null,
    promotion.departments_count > 0
      ? `${promotion.departments_count} departamentos`
      : null,
    promotion.excluded_products_count > 0
      ? `${promotion.excluded_products_count} excluidos`
      : null,
  ].filter(Boolean)

  return rules.join(' · ') || 'Sin reglas registradas'
}

export function Promotions() {
  const canAction = useSessionStore((state) => state.canAction)
  const companyId = useSessionStore((state) => state.user?.company_id)
  const canViewProducts = canAction('inventory.catalog.view')
  const canViewSucursals = canAction('sucursals.sucursal.view')
  const hasCatalogPrerequisites = canViewProducts && canViewSucursals
  const hasCreatePermission = canAction('promotions.promotion.create')
  const canCreate = hasCreatePermission && hasCatalogPrerequisites
  const canEdit =
    canAction('promotions.promotion.edit') && hasCatalogPrerequisites
  const canChangeStatus = canAction('promotions.promotion.status')
  const canDelete = canAction('promotions.promotion.delete')

  const [promotions, setPromotions] = useState<PromotionListItem[]>([])
  const [sucursals, setSucursals] = useState<PromotionOption[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, from: 0, to: 0 })

  const [search, setSearch] = useState('')
  const [type, setType] = useState<PromotionType | ''>('')
  const [channel, setChannel] = useState<PromotionChannel | ''>('')
  const [scopeType, setScopeType] = useState<PromotionScope | ''>('')
  const [lifecycleStatus, setLifecycleStatus] = useState<
    PromotionLifecycle | ''
  >('')
  const [productId, setProductId] = useState<number | undefined>()
  const [sucursalId, setSucursalId] = useState<number | undefined>()

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [previewing, setPreviewing] = useState<PromotionListItem | null>(null)
  const [statusTarget, setStatusTarget] = useState<PromotionListItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PromotionListItem | null>(null)
  const [pendingAction, setPendingAction] = useState<
    'status' | 'delete' | null
  >(null)
  const [statusConflicts, setStatusConflicts] = useState<PromotionConflict[]>([])
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)

  useEffect(() => {
    if (!canViewSucursals) {
      setSucursals([])
      return
    }

    let active = true
    const loadSucursals = async () => {
      try {
        const response = await sucursalService.getSucursales({
          company_id: companyId ?? undefined,
          per_page: 100,
        })
        if (active) setSucursals(normalizeSucursals(response))
      } catch {
        if (active) setSucursals([])
      }
    }

    void loadSucursals()
    return () => {
      active = false
    }
  }, [canViewSucursals, companyId])

  useEffect(() => {
    let active = true
    const loadPromotions = async () => {
      setIsLoading(true)
      setLoadError(false)

      try {
        const response = await promotionService.list({
          search: search || undefined,
          type: type || undefined,
          channel: channel || undefined,
          scope_type: scopeType || undefined,
          lifecycle_status: lifecycleStatus || undefined,
          product_id: productId,
          sucursal_id: sucursalId,
          page: currentPage,
          per_page: itemsPerPage,
        })

        if (!active) return
        setPromotions(response.data)
        setPagination({
          total: response.total,
          from: response.from ?? 0,
          to: response.to ?? 0,
        })
      } catch {
        if (!active) return
        setPromotions([])
        setPagination({ total: 0, from: 0, to: 0 })
        setLoadError(true)
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void loadPromotions()
    return () => {
      active = false
    }
  }, [
    channel,
    currentPage,
    lifecycleStatus,
    productId,
    refreshKey,
    scopeType,
    search,
    sucursalId,
    type,
  ])

  const resetPage = () => setCurrentPage(1)
  const refresh = () => setRefreshKey((value) => value + 1)

  const openCreate = () => {
    setEditingId(null)
    setFormOpen(true)
  }

  const openEdit = (promotion: PromotionListItem) => {
    setEditingId(promotion.id)
    setFormOpen(true)
  }

  const executeStatusChange = async () => {
    if (!statusTarget) return
    setPendingAction('status')

    try {
      await promotionService.updateStatus(
        statusTarget.id,
        !statusTarget.is_active,
      )
      toast.success('Estado de la promoción actualizado')
      setStatusTarget(null)
      refresh()
    } catch (error) {
      const response = isAxiosError<PromotionApiError>(error)
        ? error.response?.data
        : undefined
      const conflicts = response?.errors?.conflicts

      if (Array.isArray(conflicts)) {
        setStatusConflicts(conflicts as PromotionConflict[])
        setStatusTarget(null)
        setConflictDialogOpen(true)
      } else {
        toast.error(
          response?.message || 'No fue posible actualizar la promoción',
        )
      }
    } finally {
      setPendingAction(null)
    }
  }

  const executeDelete = async () => {
    if (!deleteTarget) return
    setPendingAction('delete')

    try {
      await promotionService.remove(deleteTarget.id)
      toast.success('Promoción eliminada exitosamente')
      setDeleteTarget(null)
      if (promotions.length === 1 && currentPage > 1) {
        setCurrentPage((page) => page - 1)
      } else {
        refresh()
      }
    } catch (error) {
      const message = isAxiosError<PromotionApiError>(error)
        ? error.response?.data.message
        : undefined
      toast.error(message || 'No fue posible eliminar la promoción')
    } finally {
      setPendingAction(null)
    }
  }

  const shownFilters: FilterType[] = [
    'search',
    ...(canViewProducts ? (['product'] as FilterType[]) : []),
  ]
  const hasFilters = Boolean(
    search ||
      type ||
      channel ||
      scopeType ||
      lifecycleStatus ||
      productId ||
      sucursalId,
  )

  const clearFilters = () => {
    setSearch('')
    setType('')
    setChannel('')
    setScopeType('')
    setLifecycleStatus('')
    setProductId(undefined)
    setSucursalId(undefined)
    resetPage()
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-header-foreground text-2xl font-semibold">
            Promociones y Descuentos
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Administra reglas de descuento para POS y ecommerce.
          </p>
        </div>

        {hasCreatePermission && (
          <Button
            type="button"
            onClick={openCreate}
            disabled={!canCreate}
            title={
              canCreate
                ? 'Nueva promoción'
                : 'Requiere permisos para consultar productos y sucursales'
            }
          >
            <Plus className="mr-2 h-4 w-4" /> Nueva promoción
          </Button>
        )}
      </div>

      <GlobalFilterBar
        show={shownFilters}
        values={{ search, product: productId?.toString() ?? '' }}
        onChange={(changes) => {
          if (changes.search !== undefined) setSearch(changes.search)
          if (changes.product !== undefined) {
            setProductId(changes.product ? Number(changes.product) : undefined)
          }
          resetPage()
        }}
        customOptions={{
          searchPlaceholder: 'Buscar por nombre o descripción...',
          productPlaceholder: 'Todos los productos',
        }}
      >
        <Select
          value={type || 'all'}
          onValueChange={(value) => {
            setType(value === 'all' ? '' : (value as PromotionType))
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Filtrar por tipo">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="percentage">Porcentaje</SelectItem>
            <SelectItem value="fixed_amount">Importe fijo</SelectItem>
            <SelectItem value="buy_x_pay_y">Compra X y paga Y</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={channel || 'all'}
          onValueChange={(value) => {
            setChannel(value === 'all' ? '' : (value as PromotionChannel))
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Filtrar por canal">
            <SelectValue placeholder="Todos los canales" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los canales</SelectItem>
            <SelectItem value="pos">POS</SelectItem>
            <SelectItem value="ecommerce">Ecommerce</SelectItem>
            <SelectItem value="both">Ambos</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={scopeType || 'all'}
          onValueChange={(value) => {
            setScopeType(value === 'all' ? '' : (value as PromotionScope))
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Filtrar por alcance">
            <SelectValue placeholder="Todos los alcances" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los alcances</SelectItem>
            <SelectItem value="global">Global</SelectItem>
            <SelectItem value="branches">Sucursales seleccionadas</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={lifecycleStatus || 'all'}
          onValueChange={(value) => {
            setLifecycleStatus(
              value === 'all' ? '' : (value as PromotionLifecycle),
            )
            resetPage()
          }}
        >
          <SelectTrigger aria-label="Filtrar por estado">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="scheduled">Programadas</SelectItem>
            <SelectItem value="active">Activas</SelectItem>
            <SelectItem value="expired">Expiradas</SelectItem>
            <SelectItem value="inactive">Inactivas</SelectItem>
          </SelectContent>
        </Select>

        {canViewSucursals && (
          <Select
            value={sucursalId?.toString() ?? 'all'}
            onValueChange={(value) => {
              setSucursalId(value === 'all' ? undefined : Number(value))
              resetPage()
            }}
          >
            <SelectTrigger aria-label="Filtrar por sucursal">
              <SelectValue placeholder="Todas las sucursales" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las sucursales</SelectItem>
              {sucursals.map((sucursal) => (
                <SelectItem key={sucursal.id} value={sucursal.id.toString()}>
                  {sucursal.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {hasFilters && (
          <Button type="button" variant="outline" onClick={clearFilters}>
            Limpiar filtros
          </Button>
        )}
      </GlobalFilterBar>

      {isLoading ? (
        <TableSkeleton
          headers={[
            'Promoción',
            'Descuento',
            'Canal',
            'Alcance',
            'Productos',
            'Vigencia',
            'Estado',
            'Acciones',
          ]}
          columns={[
            'text-two-lines',
            'text',
            'badge',
            'text',
            'text-two-lines',
            'text-two-lines',
            'badge',
            'actions',
          ]}
          rows={5}
        />
      ) : loadError ? (
        <div className="border-border bg-card rounded-lg border p-10 text-center">
          <AlertCircle className="text-destructive mx-auto mb-3 h-8 w-8" />
          <p className="font-medium">No fue posible cargar las promociones</p>
          <p className="text-muted-foreground mt-1 text-sm">
            Revisa la conexión e intenta nuevamente.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={refresh}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
          </Button>
        </div>
      ) : (
        <div className="border-border bg-table-bg overflow-hidden rounded-lg border shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="border-border bg-table-header-bg border-b">
                <tr>
                  {[
                    'Promoción',
                    'Descuento',
                    'Canal',
                    'Alcance',
                    'Productos',
                    'Vigencia',
                    'Estado',
                    'Acciones',
                  ].map((header) => (
                    <th
                      key={header}
                      className="text-table-header-foreground px-4 py-3 text-left text-xs font-medium tracking-wider uppercase last:text-right"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {promotions.length > 0 ? (
                  promotions.map((promotion) => (
                    <tr key={promotion.id} className="hover:bg-table-row-hover">
                      <td className="max-w-64 px-4 py-4 align-top">
                        <p className="text-table-foreground font-medium">
                          {promotion.name}
                        </p>
                        {promotion.description && (
                          <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                            {promotion.description}
                          </p>
                        )}
                      </td>
                      <td className="text-table-foreground px-4 py-4 align-top text-sm">
                        <p className="font-medium">
                          {formatPromotionRule(promotion)}
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {promotionTypeLabels[promotion.type]}
                        </p>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Badge variant="secondary">
                          {promotionChannelLabels[promotion.channel]}
                        </Badge>
                      </td>
                      <td className="max-w-52 px-4 py-4 align-top text-sm">
                        <p className="text-table-foreground font-medium">
                          {scopeSummary(promotion)}
                        </p>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {promotionScopeLabels[promotion.scope_type]}
                        </p>
                      </td>
                      <td className="max-w-64 px-4 py-4 align-top text-sm">
                        <button
                          type="button"
                          onClick={() => setPreviewing(promotion)}
                          className="text-primary hover:underline font-medium"
                        >
                          {promotion.applicable_products_count}{' '}
                          {promotion.applicable_products_count === 1
                            ? 'producto aplicable'
                            : 'productos aplicables'}
                        </button>
                        <p className="text-muted-foreground mt-1 max-w-60 whitespace-normal text-xs">
                          {applicabilitySummary(promotion)}
                        </p>
                      </td>
                      <td className="text-table-foreground px-4 py-4 align-top text-xs">
                        <p>
                          {formatDate(
                            toPromotionStartDateValue(promotion.starts_at),
                          )}
                        </p>
                        <p className="text-muted-foreground mt-1">
                          hasta{' '}
                          {formatDate(
                            toPromotionEndDateValue(promotion.ends_at),
                          )}
                        </p>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <Badge
                          className={
                            lifecycleBadgeClasses[promotion.lifecycle_status]
                          }
                        >
                          {promotionLifecycleLabels[promotion.lifecycle_status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setPreviewing(promotion)}
                            title="Ver productos aplicables"
                            aria-label={`Ver productos aplicables de ${promotion.name}`}
                            className="text-primary hover:bg-primary/10 rounded p-1.5"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {canChangeStatus && (
                            <button
                              type="button"
                              onClick={() => setStatusTarget(promotion)}
                              title={
                                promotion.is_active ? 'Desactivar' : 'Activar'
                              }
                              className="text-primary hover:bg-primary/10 rounded p-1.5"
                            >
                              {promotion.is_active ? (
                                <ToggleRight className="h-5 w-5" />
                              ) : (
                                <ToggleLeft className="h-5 w-5" />
                              )}
                            </button>
                          )}
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => openEdit(promotion)}
                              title="Editar"
                              className="text-table-foreground hover:bg-muted rounded p-1.5"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(promotion)}
                              title="Eliminar"
                              className="text-destructive hover:bg-destructive/10 rounded p-1.5"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <Tag className="text-muted-foreground/40 mx-auto mb-3 h-8 w-8" />
                      <p className="text-muted-foreground">
                        No se encontraron promociones
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {hasCatalogPrerequisites && (
        <PromotionFormModal
          open={formOpen}
          promotionId={editingId}
          onOpenChange={setFormOpen}
          onSaved={refresh}
        />
      )}

      <PromotionProductsDialog
        promotion={previewing}
        onClose={() => setPreviewing(null)}
      />

      <ConfirmationModal
        isOpen={statusTarget !== null}
        onClose={() => setStatusTarget(null)}
        onConfirm={executeStatusChange}
        preset="toggleStatus"
        itemName="promoción"
        description={
          statusTarget
            ? `¿Deseas ${statusTarget.is_active ? 'desactivar' : 'activar'} “${statusTarget.name}”? Al activar se volverán a validar los traslapes.`
            : undefined
        }
        confirmText={statusTarget?.is_active ? 'Sí, desactivar' : 'Sí, activar'}
        isLoading={pendingAction === 'status'}
      />

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="promoción"
        description={
          deleteTarget
            ? `¿Deseas eliminar “${deleteTarget.name}”? Esta acción no se puede deshacer.`
            : undefined
        }
        isLoading={pendingAction === 'delete'}
      />

      <Dialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>No se pudo activar la promoción</DialogTitle>
            <DialogDescription>
              La API detectó los siguientes traslapes.
            </DialogDescription>
          </DialogHeader>
          <PromotionConflictDetails
            conflicts={statusConflicts}
            sucursals={sucursals}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
